import { useEffect, useRef } from "react";

import type { AnalyticsEventContext } from "../services/analytics-api";
import {
  useCreatePageViewEventMutation,
  useCreateAnalyticsEventMutation,
} from "../services/analytics-api";
import { useToken } from "../utils/context";

export interface AnalyticsOptions {
  context?: AnalyticsEventContext;
  meta?: Record<string, unknown>;
}

export const useAnalytics = (options: AnalyticsOptions = {}) => {
  const { accessToken } = useToken();
  const [createPageViewEvent] = useCreatePageViewEventMutation();
  const [createAnalyticsEvent] = useCreateAnalyticsEventMutation();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPageFocused = useRef(true);

  const trackPageView = async (
    url?: string,
    meta?: Record<string, unknown>,
    context?: AnalyticsEventContext,
  ) => {
    if (!accessToken) return;

    try {
      await createPageViewEvent({
        url: url || window.location.href,
        meta: {
          ...meta,
          userAgent: navigator.userAgent,
          screenResolution: `${screen.width}x${screen.height}`,
          timestamp: new Date().toISOString(),
        },
        context: { ...options.context, ...context },
      });
    } catch (error) {
      console.error("Failed to track analytics event:", error);
    }
  };

  const trackEvent = async (
    type: string,
    url?: string,
    meta?: Record<string, unknown>,
    context?: AnalyticsEventContext,
  ) => {
    if (!accessToken) return;

    try {
      await createAnalyticsEvent({
        type,
        url,
        meta: {
          ...meta,
          userAgent: navigator.userAgent,
          screenResolution: `${screen.width}x${screen.height}`,
          timestamp: new Date().toISOString(),
        },
        context: { ...options.context, ...context },
      });
    } catch (error) {
      console.error("Failed to track analytics event:", error);
    }
  };

  const startTracking = () => {
    // Track initial page view
    trackPageView();

    // Set up interval for tracking every 30 seconds when page is focused
    intervalRef.current = setInterval(() => {
      if (isPageFocused.current) {
        trackPageView();
      }
    }, 30000); // 30 seconds

    // Track when page becomes visible/focused
    const handleVisibilityChange = () => {
      if (!document.hidden && isPageFocused.current) {
        trackPageView();
      }
    };

    const handleFocus = () => {
      isPageFocused.current = true;
      trackPageView();
    };

    const handleBlur = () => {
      isPageFocused.current = false;
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  };

  useEffect(() => {
    if (!accessToken) return;

    const cleanup = startTracking();
    return cleanup;
  }, [accessToken]);

  return {
    trackPageView,
    trackEvent,
  };
};
